import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const TYPE_STYLES = {
  matrix:           { label: "Matrix edit",     cls: "bg-blue-100 text-blue-700" },
  matrix_reset:     { label: "Matrix reset",    cls: "bg-amber-100 text-amber-700" },
  override:         { label: "User override",   cls: "bg-emerald-100 text-emerald-700" },
  override_cleared: { label: "Override cleared", cls: "bg-slate-100 text-slate-600" },
};

function DetailLine({ entry, labels }) {
  const d = entry.detail || {};
  const name = (a) => labels?.[a] || a;
  const bits = [];
  if (entry.type === "matrix") bits.push(`${d.count ?? (d.changes || []).length} cell(s) changed`);
  if (d.grants_added?.length) bits.push(`granted: ${d.grants_added.map(name).join(", ")}`);
  if (d.grants_removed?.length) bits.push(`grant removed: ${d.grants_removed.map(name).join(", ")}`);
  if (d.denies_added?.length) bits.push(`denied: ${d.denies_added.map(name).join(", ")}`);
  if (d.denies_removed?.length) bits.push(`deny removed: ${d.denies_removed.map(name).join(", ")}`);
  return <span className="text-xs text-slate-500">{bits.join(" · ") || "—"}</span>;
}

export default function HistoryTab() {
  const [items, setItems] = useState(null);
  const [labels, setLabels] = useState({});

  useEffect(() => {
    api.get("/permissions/history?limit=200")
      .then(({ data }) => { setItems(data.items || []); setLabels(data.labels || {}); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (items === null) return <div className="py-12 text-center text-sm text-slate-400">Loading history…</div>;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white" data-testid="history-tab">
      {items.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-400" data-testid="history-empty">
          No permission changes recorded yet.
        </div>
      ) : (
        <div className="divide-y divide-slate-50">
          {items.map((e) => {
            const t = TYPE_STYLES[e.type] || { label: e.type, cls: "bg-slate-100 text-slate-600" };
            return (
              <div key={e.id} className="flex flex-wrap items-start gap-3 px-4 py-3" data-testid={`history-row-${e.id}`}>
                <Badge className={`${t.cls} hover:${t.cls}`}>{t.label}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800">
                    <span className="font-medium">{e.target}</span>
                    {e.reason && <span className="ml-2 italic text-slate-400">“{e.reason}”</span>}
                  </div>
                  <DetailLine entry={e} labels={labels} />
                </div>
                <div className="text-right font-mono-alt text-[10px] text-slate-400">
                  <div>{e.by}</div>
                  <div>{e.at ? new Date(e.at).toLocaleString() : ""}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
